import type { Pwf } from "../api/pwf";

/** Same heuristic LifeOsSeeder/Compass/Rituals use: first non-template,
 *  parentless pwf whose config carries a `cockpit` key. */
const compassOf = (pwfs: Pwf[]) =>
  pwfs.find(
    (p) => !p.isTemplate && !p.pwfMeta?.fiParent && Boolean((p.config as any)?.cockpit)
  ) ?? null;

export interface CockpitState {
  compassId: string | null;
  seededAt: string | null;
  plantedCollapsedAt: string | null;
  season: { line: string; setAt: string; source: string } | null;
  weekFocus: { line: string; setAt: string } | null;
  /** ISO time of the most recent weekly review that closed (ritualLog). */
  lastWeeklyReviewAt: string | null;
  /** Max simultaneously-active life projects (user-adjustable via the weekly
   *  ritual's set_active_cap; anything invalid falls back to 3). */
  activeCap: number;
}

const lastWeeklyReviewAt = (ritualLog: unknown): string | null => {
  if (!Array.isArray(ritualLog)) return null;
  let latest = -Infinity;
  for (const e of ritualLog as Array<{ ritual?: string; at?: number; date?: string }>) {
    if (e?.ritual !== "weekly") continue;
    const ts = typeof e.at === "number" ? e.at : e.date ? new Date(e.date).getTime() : NaN;
    if (Number.isFinite(ts) && ts > latest) latest = ts;
  }
  return Number.isFinite(latest) ? new Date(latest).toISOString() : null;
};

export function readCockpitState(pwfs: Pwf[]): CockpitState {
  const compass = compassOf(pwfs ?? []);
  const cockpit = ((compass?.config as any)?.cockpit ?? {}) as Record<string, any>;
  return {
    compassId: compass?._id ?? null,
    seededAt: cockpit.seededAt ?? null,
    plantedCollapsedAt: cockpit.plantedCollapsedAt ?? null,
    season: cockpit.season ?? null,
    weekFocus: cockpit.weekFocus ?? null,
    lastWeeklyReviewAt: lastWeeklyReviewAt(cockpit.ritualLog),
    activeCap:
      Number.isInteger(cockpit.activeCap) && cockpit.activeCap >= 1 && cockpit.activeCap <= 6
        ? (cockpit.activeCap as number)
        : 3,
  };
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

/** Whether the compass's current week focus line is still worth showing as
 *  "this week's focus" — a weekly review that closes without a fresh
 *  intention leaves weekFocus untouched, so "a review happened recently"
 *  (lastWeeklyReviewAt) is NOT the same fact as "this focus line is recent." */
export function isWeekFocusFresh(
  weekFocus: CockpitState["weekFocus"],
  now: number = Date.now()
): boolean {
  if (!weekFocus?.setAt) return false;
  const setAt = new Date(weekFocus.setAt).getTime();
  return Number.isFinite(setAt) && now - setAt < WEEK_MS;
}
